import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import { getExternalContracts } from "../../../setup/api/services/contract";
import { getActionsHistory } from "../../../setup/api/services/contractActions";
import { ReactComponent as LeftArrow } from "../../../assets/svg/leftArrow.svg";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import Pagination from "../pagination";
import { ROUTES } from "../../../setup/constants";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumbs from "../breadCrumbs";
import ContractsListItem from "../contractListItem";
import { ActivityMapped } from "../../../setup/api/services/contractActions/models";
import { getBasicUserDetailsById } from "../../../setup/api/services/user";
import {
  EContractType,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";
import { IUserInfoResponseData } from "../../../setup/api/services/user/models";
import { mapActivities } from "../updates";
import classNames from "classnames";
import ActivityItem from "../updates/ActivityItem";
import Spinner from "../../../components/common/Spinner";
import useGetContractStats from "../../../hooks/useGetContractStats";
import UserIdVerifiedIcon from "../../../components/common/idVerified";

const BusinessUserProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const userToken = AuthConfig.getToken();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const organizationCode = useOrganizationCode(userToken) || undefined;
  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [tabType, setTabType] = useState("contracts");
  const [contracts, setContracts] = useState<IContractDetailsResponseData[]>(
    []
  );
  const [activities, setActivities] = useState<ActivityMapped[]>([]);
  const [user, setUser] = useState<IUserInfoResponseData>();

  const pageSize = 10;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);

  const { totalCount: totalContractsCount } = useGetContractStats(
    id,
    organizationCode
  );

  const fetchContracts = useCallback(
    async (listType) => {
      setIsLoading(true);
      try {
        const body = {
          listType: listType,
          participantId: id,
          page: currentPage - 1,
          size: pageSize,
          organizationCode: organizationCode,
          type: EContractType.MILESTONE_CONTRACT,
        };
        const response = await getExternalContracts(body);
        setContracts(response.data.content);
        setPagesCount(response.data.totalPages);
        setTotalCount(response.data.totalElements);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    },
    [id, currentPage, organizationCode]
  );

  const fetchOrganizationHistory = useCallback(async () => {
    if (!organizationCode || !id) return;
    setIsLoading(true);
    try {
      const response = await getActionsHistory({
        organizationCode,
        actorIds: [id, `${user?.userIdentification}`],
        page: currentPage - 1,
        size: pageSize,
      });
      setPagesCount(response.data.totalPages);
      setTotalCount(response.data.totalElements);
      setActivities(mapActivities(response?.data?.content));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, id, organizationCode, user?.userIdentification]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!id || user) return;
      try {
        const response = await getBasicUserDetailsById(id);

        setUser(response?.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchUser();
  }, [id]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!tabType) return;

      if (tabType === "contracts") fetchContracts("active_no_draft");
      else fetchOrganizationHistory();
    };

    fetchUserDetails();
  }, [fetchContracts, fetchOrganizationHistory, currentPage, tabType]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const list = useMemo(() => {
    return [
      {
        id: 1,
        type: "contracts",
        title: "المعاملات",
      },
      {
        id: 2,
        type: "updates",
        title: "التحديثات",
      },
    ];
  }, []);

  const handleReadNotification = (index: number) => {
    setActivities((prevActivities) =>
      prevActivities.map((activity, i) => ({
        ...activity,
        unread: i === index ? false : activity.unread,
      }))
    );
    if (
      authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
      authorities?.includes("CLIENT_ADMIN") ||
      authorities?.includes("ORGANIZATION_OWNER")
    )
      navigate(
        ROUTES.CONTRACT_DETAILS.replace(
          ":id",
          activities?.[index]?.contractId || ""
        )
      );
    // TODO: API to mark as read
  };

  return (
    <>
      <div className={styles.breadcrumbContainer}>
        <BreadCrumbs
          activeIndex={2}
          separator={<LeftArrow />}
          items={[
            {
              label: t("lbl_business_home"),
              onClick: () => navigate(ROUTES.HOME),
              visited: true,
            },
            {
              label: t("lbl_customers"),
              onClick: () => navigate(ROUTES.USERS_MANAGEMENT),
              visited: true,
            },
            {
              label: user?.profile?.fullName || "",
            },
          ]}
        />
      </div>
      <div className={styles.container}>
        <div className={styles.userInfoContainer}>
          <div className={styles.userAvatar}>
            <img
              src={user?.profile?.photoUrl || "/assets/images/svg/user.svg"}
              alt={user?.profile.fullName}
            />
          </div>
          {user?.nationalIdVerified && (
            <UserIdVerifiedIcon nationalIdVerified={true} />
          )}

          <div className={styles.userDetailsContainer}>
            <div className={styles.userNameSection}>
              <h5 className={styles.userName}>{user?.profile?.fullName}</h5>
              <p className={styles.userIdentification}>{user?.email}</p>
            </div>
            <div className={styles.userInfoRow}>
              <div className={styles.userInfoItem}>
                <p className={styles.userInfoLabel}>رقم الجوال</p>
                <p className={styles.phoneNumberValue}>{user?.phoneNumber}</p>
              </div>
              <div className={styles.userInfoItem}>
                <p className={styles.userInfoLabel}>عدد المعاملات</p>
                <p className={styles.userInfoValue}>
                  {totalContractsCount || 0}
                </p>
              </div>
            </div>
          </div>

          <div
            className={classNames(
              styles.statusBadge,
              user?.nationalIdVerified
                ? styles.statusApproved
                : styles.statusNotApproved
            )}>
            {user?.nationalIdVerified ? "موثق" : "غير موثق"}
          </div>
        </div>

        <div className={styles.tabContainer}>
          {list?.map((listItem) => (
            <li
              onClick={() => {
                setTabType(listItem?.type);
                handlePageChange(1);
              }}
              key={listItem?.title}
              className={classNames(
                styles.listItem,
                {
                  [styles.listItemSelected]: listItem?.type === tabType,
                },
                { [styles.firstItem]: listItem?.id === 1 },
                {
                  [styles.lastItem]:
                    listItem?.id === list?.[list?.length - 1]?.id,
                }
              )}>
              {listItem?.title}
            </li>
          ))}
        </div>

        {isLoading && (
          <div className={styles.loadingContainer}>
            <Spinner />
          </div>
        )}
        {tabType === "updates" && !isLoading && (
          <div className={styles.updatesFeedContainer}>
            {activities.map((activity, index) => (
              <ActivityItem
                key={activity.id}
                item={activity}
                onClick={() => handleReadNotification(index)}
              />
            ))}
          </div>
        )}

        {tabType === "contracts" &&
          !isLoading &&
          (authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
            authorities?.includes("CLIENT_ADMIN") ||
            authorities?.includes("ORGANIZATION_OWNER")) && (
            <div className={styles.contractsContainer}>
              {contracts?.map((contract) => (
                <ContractsListItem
                  key={(contract as { id: string })?.id}
                  title={contract?.itemDetail?.title}
                  description={contract?.itemDetail?.description}
                  shortId={contract?.shortId}
                  status={contract?.status}
                  onClick={() =>
                    navigate(
                      ROUTES.CONTRACT_DETAILS?.replace(":id", contract?.id),
                      { state: { from: location.pathname } }
                    )
                  }
                  partiesCount={contract?.parties?.length}
                  itemPrice={contract?.itemPrice}
                />
              ))}
            </div>
          )}

        <div className={styles.paginationContainer}>
          <Pagination
            countName={
              tabType === "contracts" ? t("lbl_project") : t("lbl_update")
            }
            pagesCount={pagesCount}
            length={totalCount}
            page={currentPage}
            pageSize={pageSize}
            handlePaginationChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
};

export default BusinessUserProfile;
